import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Navigate } from "react-router-dom";
import { isCrmAdmin } from "../common/crmRoles";
import {
    Box, Typography, Card, Button, CircularProgress, Alert,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    IconButton, Tooltip, Table, TableHead, TableBody, TableRow, TableCell,
    TableContainer,
} from "@mui/material";
import {
    AddCircleOutline, EditOutlined, Close, LocalOfferOutlined, Refresh,
} from "@mui/icons-material";
import { WB_SX } from "../../../ui/ux/uiTokens";
import axiosInstance from "../../../api/axiosInstance";
import base_url from "../../../constants";
import useNotification from "../../common/Notification";

const TAG_COLOR_PALETTE = [
    { bg: "#fef3c7", text: "#92400e" },
    { bg: "#e0e7ff", text: "#3730a3" },
    { bg: "#d1fae5", text: "#065f46" },
    { bg: "#fce7f3", text: "#9d174d" },
    { bg: "#dbeafe", text: "#1d4ed8" },
    { bg: "#ffe4e6", text: "#be123c" },
];

const normaliseTags = (raw) => {
    if (Array.isArray(raw?.data)) return raw.data;
    if (Array.isArray(raw)) return raw;
    return [];
};

const getTid = (t) => t?.id;

// ─── Create tag ───────────────────────────────────────────────────────────────
const CreateTagDialog = ({ open, onClose, onCreated }) => {
    const notify = useNotification();
    const [name, setName] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) setName("");
    }, [open]);

    const handleSubmit = async () => {
        if (!name.trim()) return;
        setSaving(true);
        try {
            await axiosInstance.post(`${base_url}/crm/contact-tags`, { tag_name: name.trim() });
            notify("Tag created successfully", "success");
            onCreated();
            setName("");
            onClose();
        } catch (err) {
            notify(err?.response?.data?.message || "Failed to create tag", "error");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem", pb: 1 }}>Create tag</DialogTitle>
            <DialogContent sx={{ pt: "8px !important" }}>
                <TextField
                    autoFocus
                    fullWidth
                    label="Tag name"
                    placeholder="e.g. VIP, Premium, Lead…"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                    size="small"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                <Button onClick={onClose} size="small" sx={WB_SX.secondaryButton}>Cancel</Button>
                <Button variant="contained" size="small" disabled={!name.trim() || saving} onClick={handleSubmit} sx={WB_SX.primaryButton}>
                    {saving ? "Creating…" : "Create"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ─── Rename tag ───────────────────────────────────────────────────────────────
const RenameTagDialog = ({ open, tag, onClose, onRenamed }) => {
    const notify = useNotification();
    const [name, setName] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => { setName(tag?.tag_name || ""); }, [tag]);

    const handleSubmit = async () => {
        const tid = getTid(tag);
        if (!name.trim() || tid == null) return;
        setSaving(true);
        try {
            await axiosInstance.put(`${base_url}/crm/contact-tags/${tid}`, { tag_name: name.trim() });
            notify("Tag updated", "success");
            onRenamed(tid, name.trim());
            onClose();
        } catch (err) {
            notify(err?.response?.data?.message || "Failed to update tag", "error");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem", pb: 1 }}>Update tag</DialogTitle>
            <DialogContent sx={{ pt: "8px !important" }}>
                <TextField
                    autoFocus
                    fullWidth
                    label="Tag name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                    size="small"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                <Button onClick={onClose} size="small" sx={WB_SX.secondaryButton}>Cancel</Button>
                <Button variant="contained" size="small" disabled={!name.trim() || saving} onClick={handleSubmit} sx={WB_SX.primaryButton}>
                    {saving ? "Saving…" : "Save"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ─── Page — restricted to CRM-ADMIN (see navConfig) ───────────────────────────
const CRMContactTags = () => {
    const userIsCrmAdmin = useMemo(() => isCrmAdmin(), []);
    const _notify = useNotification();
    const notifyRef = useRef(_notify);
    notifyRef.current = _notify;

    const [tags, setTags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [renameTag, setRenameTag] = useState(null);

    const fetchTags = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get(`${base_url}/crm/contact-tags`);
            setTags(normaliseTags(res.data));
        } catch (err) {
            const msg = err?.response?.data?.message || "Failed to load contact tags";
            setError(msg);
            notifyRef.current(msg, "error");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!userIsCrmAdmin) {
            setLoading(false);
            return;
        }
        fetchTags();
    }, [fetchTags, userIsCrmAdmin]);

    const handleRenamed = (id, newName) => {
        setTags((prev) => prev.map((t) => (getTid(t) === id ? { ...t, tag_name: newName } : t)));
    };

    if (!userIsCrmAdmin) {
        return <Navigate to="/crm/dashboard" replace />;
    }

    return (
        <Box sx={WB_SX.page}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3, flexWrap: "wrap", gap: 2 }}>
                <Box>
                    <Typography variant="h5" sx={WB_SX.pageTitle}>Manage contact tags</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Create and rename tags for your contacts. Assign tags when adding or editing a contact.
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Tooltip title="Refresh list">
                        <IconButton size="small" onClick={() => fetchTags()} disabled={loading} sx={WB_SX.secondaryButton}>
                            <Refresh fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Button variant="contained" size="small" startIcon={<AddCircleOutline fontSize="small" />} onClick={() => setCreateOpen(true)} sx={WB_SX.primaryButton}>
                        Create tag
                    </Button>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            <Card elevation={0} sx={WB_SX.card}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={WB_SX.tableHeadCell} width={72}>#</TableCell>
                                <TableCell sx={WB_SX.tableHeadCell}>Tag name</TableCell>
                                <TableCell sx={WB_SX.tableHeadCell} align="right" width={80}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={3} align="center" sx={{ py: 6 }}>
                                        <CircularProgress size={28} />
                                    </TableCell>
                                </TableRow>
                            ) : tags.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={3} align="center" sx={{ py: 6 }}>
                                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
                                            <LocalOfferOutlined sx={{ fontSize: 40, color: "text.disabled" }} />
                                            <Typography variant="body2" color="text.secondary">No tags yet. Create your first tag to label contacts.</Typography>
                                            <Button size="small" variant="contained" onClick={() => setCreateOpen(true)} sx={WB_SX.primaryButton}>
                                                Create tag
                                            </Button>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                tags.map((t, i) => {
                                    const tid = getTid(t);
                                    const c = TAG_COLOR_PALETTE[i % TAG_COLOR_PALETTE.length];
                                    return (
                                        <TableRow key={tid} sx={{ ...WB_SX.tableRow, background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                                            <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>{i + 1}</TableCell>
                                            <TableCell>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: c.text }} />
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{t.tag_name}</Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Tooltip title="Update tag">
                                                    <IconButton size="small" onClick={() => setRenameTag(t)} sx={{ color: "text.secondary", "&:hover": { color: "#2563eb" } }}>
                                                        <EditOutlined fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>

            <CreateTagDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={fetchTags} />
            <RenameTagDialog
                open={!!renameTag}
                tag={renameTag}
                onClose={() => setRenameTag(null)}
                onRenamed={handleRenamed}
            />
        </Box>
    );
};

export default CRMContactTags;
